import { S3Client, CreateMultipartUploadCommand, UploadPartCommand, CompleteMultipartUploadCommand, AbortMultipartUploadCommand, ListPartsCommand, ListObjectsV2Command, GetObjectCommand, DeleteObjectCommand, DeleteObjectsCommand, HeadObjectCommand } from '@aws-sdk/client-s3';
import { WASABI_CONFIG } from '../config/wasabiConfig';

// Get S3 Client with current config (dynamic to pick up env changes)
const getS3Client = (): S3Client => {
  // Debug: Log S3 client config
  if (process.env.NODE_ENV === 'development') {
    console.log('🚀 Creating S3Client with:', {
      region: WASABI_CONFIG.REGION,
      endpoint: WASABI_CONFIG.ENDPOINT,
      bucket: WASABI_CONFIG.BUCKET,
    });
  }
  
  return new S3Client({
    region: WASABI_CONFIG.REGION,
    endpoint: WASABI_CONFIG.ENDPOINT,
    credentials: {
      accessKeyId: WASABI_CONFIG.ACCESS_KEY,
      secretAccessKey: WASABI_CONFIG.SECRET_KEY,
    },
    forcePathStyle: true, // Required for Wasabi
  });
};

export interface UploadPart {
  PartNumber: number;
  ETag: string;
}

export interface MultipartUploadState {
  uploadId: string;
  key: string;
  bucket: string;
  parts: UploadPart[];
  uploadedChunks: Set<number>;
}

const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks
const STORAGE_KEY_PREFIX = 'wasabi_upload_';
const METADATA_STORAGE_PREFIX = 'wasabi_asset_metadata_';

// Store upload state in localStorage for resumability
export const saveUploadState = (fileId: string, state: MultipartUploadState) => {
  try {
    // Convert Set to Array for JSON serialization
    const serializableState = {
      ...state,
      uploadedChunks: Array.from(state.uploadedChunks),
    };
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${fileId}`, JSON.stringify(serializableState));
  } catch (error) {
    console.error('Failed to save upload state:', error);
  }
};

export const getUploadState = (fileId: string): MultipartUploadState | null => {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${fileId}`);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored);
    // Convert Array back to Set
    return {
      ...parsed,
      uploadedChunks: new Set(parsed.uploadedChunks || []),
    };
  } catch (error) {
    console.error('Failed to get upload state:', error);
    return null;
  }
};

export const clearUploadState = (fileId: string) => {
  try {
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}${fileId}`);
  } catch (error) {
    console.error('Failed to clear upload state:', error);
  }
};

// Generate unique file ID based on file name and size (without timestamp for resumability)
export const generateFileId = (fileName: string, fileSize: number): string => {
  // Use file name and size only (no timestamp) so same file can be resumed
  return `${fileName}_${fileSize}`;
};

// Get all incomplete uploads from localStorage
export const getAllIncompleteUploads = (): Array<{ fileId: string; state: MultipartUploadState; fileName: string; fileSize: number }> => {
  const incompleteUploads: Array<{ fileId: string; state: MultipartUploadState; fileName: string; fileSize: number }> = [];
  
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        const fileId = key.replace(STORAGE_KEY_PREFIX, '');
        const state = getUploadState(fileId);
        if (state) {
          // Extract file name from key (format: digital-assets/timestamp/filename.zip)
          const keyParts = state.key.split('/');
          const fileName = keyParts[keyParts.length - 1] || 'Unknown';
          // Try to get file size from metadata or estimate from chunks
          const fileSize = state.uploadedChunks.size * CHUNK_SIZE; // Approximate
          incompleteUploads.push({ fileId, state, fileName, fileSize });
        }
      }
    }
  } catch (error) {
    console.error('Failed to get incomplete uploads:', error);
  }
  
  return incompleteUploads;
};

// Check if a file can be resumed
export const canResumeUpload = (fileName: string, fileSize: number): MultipartUploadState | null => {
  const fileId = generateFileId(fileName, fileSize);
  return getUploadState(fileId);
};

// Initialize multipart upload
export const initMultipartUpload = async (
  fileName: string,
  fileSize: number,
  fileType: string = 'application/zip'
): Promise<MultipartUploadState> => {
  // Generate unique key/path for file
  const timestamp = Date.now();
  const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
  const key = `digital-assets/${timestamp}/${sanitizedFileName}`;

  const command = new CreateMultipartUploadCommand({
    Bucket: WASABI_CONFIG.BUCKET,
    Key: key,
    ContentType: fileType,
    Metadata: {
      'original-name': fileName,
      'file-size': fileSize.toString(),
    },
  });

  const s3Client = getS3Client();
  const response = await s3Client.send(command);

  if (!response.UploadId) {
    throw new Error('Failed to initialize multipart upload');
  }

  const state: MultipartUploadState = {
    uploadId: response.UploadId,
    key,
    bucket: WASABI_CONFIG.BUCKET,
    parts: [],
    uploadedChunks: new Set(),
  };

  return state;
};

// Upload a single chunk
export const uploadChunk = async (
  state: MultipartUploadState,
  chunkIndex: number,
  chunkData: Blob
): Promise<UploadPart> => {
  const partNumber = chunkIndex + 1; // S3 part numbers start from 1

  // Convert Blob to Uint8Array for AWS SDK compatibility
  const arrayBuffer = await chunkData.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);

  const command = new UploadPartCommand({
    Bucket: state.bucket,
    Key: state.key,
    PartNumber: partNumber,
    UploadId: state.uploadId,
    Body: uint8Array, // Use Uint8Array which AWS SDK accepts
  });

  const s3Client = getS3Client();
  const response = await s3Client.send(command);

  if (!response.ETag) {
    throw new Error(`Failed to upload chunk ${chunkIndex}`);
  }

  const part: UploadPart = {
    PartNumber: partNumber,
    ETag: response.ETag,
  };

  // Update state
  state.parts.push(part);
  state.uploadedChunks.add(chunkIndex);
  state.parts.sort((a, b) => a.PartNumber - b.PartNumber);

  return part;
};

// Complete multipart upload
export const completeMultipartUpload = async (
  state: MultipartUploadState
): Promise<string> => {
  if (state.parts.length === 0) {
    throw new Error('No parts to complete upload');
  }

  const command = new CompleteMultipartUploadCommand({
    Bucket: state.bucket,
    Key: state.key,
    UploadId: state.uploadId,
    MultipartUpload: {
      Parts: state.parts,
    },
  });

  const s3Client = getS3Client();
  
  try {
    const response = await s3Client.send(command);

    if (!response.Location && !response.Key) {
      throw new Error('Failed to complete multipart upload');
    }

    // Generate file URL
    const fileUrl = `${WASABI_CONFIG.ENDPOINT}/${state.bucket}/${state.key}`;
    return fileUrl;
  } catch (error: any) {
    // Handle case where upload is already completed (network drop scenario)
    // If upload was completed on Wasabi but frontend didn't receive response
    if (error.name === 'NoSuchUpload' || 
        error.message?.includes('not found') ||
        error.Code === 'NoSuchUpload') {
      // Upload already completed on Wasabi - return URL anyway
      console.log('⚠️ Upload already completed on Wasabi (network drop recovery)');
      const fileUrl = `${WASABI_CONFIG.ENDPOINT}/${state.bucket}/${state.key}`;
      return fileUrl;
    }
    // Re-throw other errors
    throw error;
  }
};

// Abort multipart upload
export const abortMultipartUpload = async (state: MultipartUploadState) => {
  try {
    const command = new AbortMultipartUploadCommand({
      Bucket: state.bucket,
      Key: state.key,
      UploadId: state.uploadId,
    });

    const s3Client = getS3Client();
    await s3Client.send(command);
  } catch (error) {
    console.error('Failed to abort upload:', error);
  }
};

// Resume upload - check existing parts
export const resumeMultipartUpload = async (
  state: MultipartUploadState
): Promise<Set<number>> => {
  try {
    const command = new ListPartsCommand({
      Bucket: state.bucket,
      Key: state.key,
      UploadId: state.uploadId,
    });

    const s3Client = getS3Client();
    const response = await s3Client.send(command);
    const uploadedParts = new Set<number>();

    if (response.Parts) {
      response.Parts.forEach((part) => {
        if (part.PartNumber) {
          const chunkIndex = part.PartNumber - 1; // Convert to 0-based index
          uploadedParts.add(chunkIndex);

          // Update state with existing parts
          if (part.ETag) {
            const existingPart = state.parts.find((p) => p.PartNumber === part.PartNumber);
            if (!existingPart) {
              state.parts.push({
                PartNumber: part.PartNumber,
                ETag: part.ETag,
              });
            }
          }
        }
      });

      state.parts.sort((a, b) => a.PartNumber - b.PartNumber);
      state.uploadedChunks = uploadedParts;
    }

    return uploadedParts;
  } catch (error) {
    console.error('Failed to resume upload:', error);
    return new Set();
  }
};

// Main upload function with resumability
export const uploadFileToWasabi = async (
  file: File,
  onProgress?: (progress: { uploaded: number; total: number; percentage: number }) => void,
  fileId?: string
): Promise<{ fileUrl: string; key: string }> => {
  const id = fileId || generateFileId(file.name, file.size);
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

  // Check for existing upload state
  let state = getUploadState(id);
  let isResuming = false;

  if (state) {
    // Resume existing upload
    isResuming = true;
    const uploadedChunks = await resumeMultipartUpload(state);
    
    if (uploadedChunks.size === totalChunks) {
      // All chunks already uploaded, just complete
      const fileUrl = await completeMultipartUpload(state);
      clearUploadState(id);
      return { fileUrl, key: state.key };
    }
  } else {
    // Start new upload
    state = await initMultipartUpload(file.name, file.size, file.type);
    saveUploadState(id, state);
  }

  // Upload chunks
  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
    // Skip if already uploaded
    if (state.uploadedChunks.has(chunkIndex)) {
      const uploaded = (chunkIndex + 1) * CHUNK_SIZE;
      const percentage = Math.min(Math.round((uploaded / file.size) * 100), 100);
      if (onProgress) {
        onProgress({
          uploaded: Math.min(uploaded, file.size),
          total: file.size,
          percentage,
        });
      }
      continue;
    }

    const start = chunkIndex * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);

    // Retry logic for failed chunks
    let retryCount = 0;
    const MAX_RETRIES = 3;
    let lastError: any = null;

    while (retryCount < MAX_RETRIES) {
      try {
        await uploadChunk(state, chunkIndex, chunk);
        saveUploadState(id, state); // Save state after each chunk

        const uploaded = Math.min(end, file.size);
        const percentage = Math.round((uploaded / file.size) * 100);

        if (onProgress) {
          onProgress({
            uploaded,
            total: file.size,
            percentage,
          });
        }
        
        // Success - break retry loop
        break;
      } catch (error) {
        lastError = error;
        retryCount++;
        console.error(`Failed to upload chunk ${chunkIndex} (attempt ${retryCount}/${MAX_RETRIES}):`, error);
        
        if (retryCount < MAX_RETRIES) {
          // Wait before retry (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        } else {
          // All retries failed - save state and throw error
          saveUploadState(id, state);
          throw new Error(`Failed to upload chunk ${chunkIndex} after ${MAX_RETRIES} attempts: ${lastError.message}`);
        }
      }
    }
  }

  // Complete upload
  const fileUrl = await completeMultipartUpload(state);
  clearUploadState(id);

  return { fileUrl, key: state.key };
};

// Upload thumbnails
export const uploadThumbnailToWasabi = async (
  thumbnail: File,
  assetKey: string,
  thumbnailIndex: number
): Promise<string> => {
  const { PutObjectCommand } = await import('@aws-sdk/client-s3');
  
  const extension = thumbnail.name.split('.').pop() || 'jpg';
  // Extract directory from asset key and add thumbnails folder
  const keyParts = assetKey.split('/');
  keyParts[keyParts.length - 1] = keyParts[keyParts.length - 1].replace(/\.zip$/, '');
  const key = `${keyParts.join('/')}/thumbnails/thumb_${thumbnailIndex}.${extension}`;

  const command = new PutObjectCommand({
    Bucket: WASABI_CONFIG.BUCKET,
    Key: key,
    Body: thumbnail,
    ContentType: thumbnail.type,
  });

  const s3Client = getS3Client();
  await s3Client.send(command);
  return `${WASABI_CONFIG.ENDPOINT}/${WASABI_CONFIG.BUCKET}/${key}`;
};

// List all files from Wasabi
export const listFilesFromWasabi = async (prefix: string = 'digital-assets/'): Promise<Array<{
  key: string;
  url: string;
  size: number;
  lastModified: Date;
  fileName: string;
}>> => {
  try {
    const s3Client = getS3Client();
    const command = new ListObjectsV2Command({
      Bucket: WASABI_CONFIG.BUCKET,
      Prefix: prefix,
    });

    const response = await s3Client.send(command);
    const files: Array<{
      key: string;
      url: string;
      size: number;
      lastModified: Date;
      fileName: string;
    }> = [];

    if (response.Contents) {
      for (const object of response.Contents) {
        // Skip thumbnails folder
        if (object.Key && !object.Key.includes('/thumbnails/') && object.Key.endsWith('.zip')) {
          const fileName = object.Key.split('/').pop() || 'Unknown';
          files.push({
            key: object.Key,
            url: `${WASABI_CONFIG.ENDPOINT}/${WASABI_CONFIG.BUCKET}/${object.Key}`,
            size: object.Size || 0,
            lastModified: object.LastModified || new Date(),
            fileName,
          });
        }
      }
    }

    return files;
  } catch (error) {
    console.error('Failed to list files from Wasabi:', error);
    throw error;
  }
};

// Check if file exists in Wasabi
export const checkFileExistsInWasabi = async (assetFilePath: string): Promise<boolean> => {
  try {
    const s3Client = getS3Client();
    
    // Extract key from full URL or path
    let key = '';
    
    if (assetFilePath.includes(WASABI_CONFIG.BUCKET)) {
      const bucketIndex = assetFilePath.indexOf(WASABI_CONFIG.BUCKET);
      key = assetFilePath.substring(bucketIndex + WASABI_CONFIG.BUCKET.length + 1);
    } else if (assetFilePath.startsWith('digital-assets/') || assetFilePath.startsWith('/digital-assets/')) {
      key = assetFilePath.replace(/^\//, '');
    } else {
      try {
        const url = new URL(assetFilePath);
        const pathParts = url.pathname.split('/').filter(p => p);
        const bucketIndex = pathParts.indexOf(WASABI_CONFIG.BUCKET);
        if (bucketIndex >= 0 && bucketIndex < pathParts.length - 1) {
          key = pathParts.slice(bucketIndex + 1).join('/');
        } else {
          key = pathParts.slice(1).join('/');
        }
      } catch {
        key = assetFilePath.replace(/^https?:\/\/[^\/]+\//, '').replace(/^[^\/]+\//, '');
      }
    }

    if (!key) {
      return false;
    }

    const headCommand = new HeadObjectCommand({
      Bucket: WASABI_CONFIG.BUCKET,
      Key: key,
    });

    try {
      await s3Client.send(headCommand);
      return true; // File exists
    } catch (error: any) {
      if (error.name === 'NotFound' || error.$metadata?.httpStatusCode === 404) {
        return false; // File does not exist
      }
      throw error; // Other error
    }
  } catch (error) {
    console.error('Error checking file existence:', error);
    return false;
  }
};

// Delete file from Wasabi
export const deleteFileFromWasabi = async (assetFilePath: string): Promise<{ success: boolean; deleted: boolean; message: string }> => {
  try {
    // Extract key from full URL or path
    let key = '';
    
    if (assetFilePath.includes(WASABI_CONFIG.BUCKET)) {
      // Extract key after bucket name
      const bucketIndex = assetFilePath.indexOf(WASABI_CONFIG.BUCKET);
      key = assetFilePath.substring(bucketIndex + WASABI_CONFIG.BUCKET.length + 1);
    } else if (assetFilePath.startsWith('digital-assets/') || assetFilePath.startsWith('/digital-assets/')) {
      // Already a key
      key = assetFilePath.replace(/^\//, '');
    } else {
      // Try to extract from URL path
      try {
        const url = new URL(assetFilePath);
        const pathParts = url.pathname.split('/').filter(p => p);
        const bucketIndex = pathParts.indexOf(WASABI_CONFIG.BUCKET);
        if (bucketIndex >= 0 && bucketIndex < pathParts.length - 1) {
          key = pathParts.slice(bucketIndex + 1).join('/');
        } else {
          // Fallback: use everything after bucket in path
          key = pathParts.slice(1).join('/');
        }
      } catch {
        // If URL parsing fails, assume it's already a key
        key = assetFilePath.replace(/^https?:\/\/[^\/]+\//, '').replace(/^[^\/]+\//, '');
      }
    }

    if (!key) {
      throw new Error('Could not extract key from asset file path');
    }

    console.log('🗑️ Deleting from Wasabi:', { key, bucket: WASABI_CONFIG.BUCKET });

    // First, verify file exists before deletion
    const fileExistsBefore = await checkFileExistsInWasabi(assetFilePath);
    if (!fileExistsBefore) {
      console.log('⚠️ File does not exist in Wasabi (may have been already deleted)');
      return {
        success: true,
        deleted: false,
        message: 'File does not exist in Wasabi (may have been already deleted)'
      };
    }

    const s3Client = getS3Client();
    
    // Delete main file
    const deleteCommand = new DeleteObjectCommand({
      Bucket: WASABI_CONFIG.BUCKET,
      Key: key,
    });

    const deleteResponse = await s3Client.send(deleteCommand);
    console.log('✅ Delete response:', {
      statusCode: deleteResponse.$metadata?.httpStatusCode,
      requestId: deleteResponse.$metadata?.requestId,
      key: key
    });

    // Verify deletion by checking if file still exists
    // Wait a moment for deletion to propagate
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const fileExistsAfter = await checkFileExistsInWasabi(assetFilePath);
    
    if (fileExistsAfter) {
      console.warn('⚠️ File still exists after delete command - deletion may not have completed');
      // Still return success as delete command was sent successfully
      // Sometimes S3 services take time to propagate deletions
    } else {
      console.log('✅ File successfully deleted and verified from Wasabi');
    }

    // Also try to delete thumbnails folder if it exists
    const keyParts = key.split('/');
    const thumbnailsKey = `${keyParts.slice(0, -1).join('/')}/thumbnails/`;
    let thumbnailsDeleted = 0;
    
    try {
      // List and delete all thumbnails in the folder
      const listCommand = new ListObjectsV2Command({
        Bucket: WASABI_CONFIG.BUCKET,
        Prefix: thumbnailsKey,
      });

      const listResponse = await s3Client.send(listCommand);
      
      if (listResponse.Contents && listResponse.Contents.length > 0) {
        const deleteObjectsCommand = new DeleteObjectsCommand({
          Bucket: WASABI_CONFIG.BUCKET,
          Delete: {
            Objects: listResponse.Contents.map(obj => ({ Key: obj.Key! })),
          },
        });

        const thumbDeleteResponse = await s3Client.send(deleteObjectsCommand);
        thumbnailsDeleted = listResponse.Contents.length;
        console.log(`✅ Deleted ${thumbnailsDeleted} thumbnail(s) from Wasabi`);
      }
    } catch (thumbError) {
      // If thumbnail deletion fails, log but don't throw (main file deletion succeeded)
      console.warn('⚠️ Failed to delete thumbnails (non-critical):', thumbError);
    }

    return {
      success: true,
      deleted: !fileExistsAfter,
      message: `File deleted successfully${thumbnailsDeleted > 0 ? ` (${thumbnailsDeleted} thumbnails also deleted)` : ''}`
    };
  } catch (error: any) {
    console.error('❌ Failed to delete from Wasabi:', error);
    return {
      success: false,
      deleted: false,
      message: `Delete failed: ${error.message || 'Unknown error'}`
    };
  }
};

// Download file directly from Wasabi
export const downloadFileFromWasabi = async (assetFilePath: string, fileName?: string): Promise<void> => {
  try {
    // Extract key from full URL
    // URL format: https://s3.eu-west-1.wasabisys.com/digitalassetsms-staging/digital-assets/1767635656411/wanderly.zip
    let key = '';
    
    if (assetFilePath.includes(WASABI_CONFIG.BUCKET)) {
      // Extract key after bucket name
      const bucketIndex = assetFilePath.indexOf(WASABI_CONFIG.BUCKET);
      key = assetFilePath.substring(bucketIndex + WASABI_CONFIG.BUCKET.length + 1);
    } else if (assetFilePath.startsWith('digital-assets/') || assetFilePath.startsWith('/digital-assets/')) {
      // Already a key
      key = assetFilePath.replace(/^\//, '');
    } else {
      // Try to extract from URL path
      try {
        const url = new URL(assetFilePath);
        const pathParts = url.pathname.split('/').filter(p => p);
        const bucketIndex = pathParts.indexOf(WASABI_CONFIG.BUCKET);
        if (bucketIndex >= 0 && bucketIndex < pathParts.length - 1) {
          key = pathParts.slice(bucketIndex + 1).join('/');
        } else {
          // Fallback: use everything after bucket in path
          key = pathParts.slice(1).join('/');
        }
      } catch {
        // If URL parsing fails, assume it's already a key
        key = assetFilePath.replace(/^https?:\/\/[^\/]+\//, '').replace(/^[^\/]+\//, '');
      }
    }

    if (!key) {
      throw new Error('Could not extract key from asset file path');
    }

    console.log('Downloading from Wasabi:', { key, bucket: WASABI_CONFIG.BUCKET });

    const s3Client = getS3Client();
    
    // Create GetObject command
    const command = new GetObjectCommand({
      Bucket: WASABI_CONFIG.BUCKET,
      Key: key,
    });

    // Download file directly as blob
    const response = await s3Client.send(command);
    
    if (!response.Body) {
      throw new Error('No file content received from Wasabi');
    }

    // Convert stream to blob
    const chunks: BlobPart[] = [];
    const reader = response.Body.transformToWebStream().getReader();
    
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        // Convert Uint8Array to ArrayBuffer for Blob compatibility
        chunks.push(value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength));
      }
    }

    // Create blob from chunks
    const blob = new Blob(chunks);
    const url = window.URL.createObjectURL(blob);

    // Trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || key.split('/').pop() || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    console.error('Failed to download from Wasabi:', error);
    throw new Error(`Download failed: ${error.message || 'Unknown error'}`);
  }
};

// Save asset metadata to localStorage
export const saveAssetMetadata = (key: string, metadata: {
  name: string;
  category?: string;
  category_id?: string;
  subcategory_id?: string | number | number[] | null;
  description?: string;
  tags: string[];
  thumbnails: string[];
  licenses: any[];
  source_platform?: string;
  platform_id?: string;
  source_url?: string;
  uploaded_at: string;
}) => {
  try {
    localStorage.setItem(`${METADATA_STORAGE_PREFIX}${key}`, JSON.stringify(metadata));
  } catch (error) {
    console.error('Failed to save asset metadata:', error);
  }
};

// Get asset metadata from localStorage
export const getAssetMetadata = (key: string): any | null => {
  try {
    const stored = localStorage.getItem(`${METADATA_STORAGE_PREFIX}${key}`);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch (error) {
    console.error('Failed to get asset metadata:', error);
    return null;
  }
};

// Get all asset metadata from localStorage
export const getAllAssetMetadata = (): Record<string, any> => {
  const metadata: Record<string, any> = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const storageKey = localStorage.key(i);
      if (storageKey && storageKey.startsWith(METADATA_STORAGE_PREFIX)) {
        const key = storageKey.replace(METADATA_STORAGE_PREFIX, '');
        const data = getAssetMetadata(key);
        if (data) {
          metadata[key] = data;
        }
      }
    }
  } catch (error) {
    console.error('Failed to get all asset metadata:', error);
  }
  return metadata;
};

